import {action, computed, toJS, _, reaction} from '@api'
import {CatalogStore, PredefinedCardViewConfig, ICardViewSettings, ICatalogFieldConfig} from '@stores'
import {ICardViewFieldSettings} from '@stores/catalog/CatalogSettingsStore'
import {synchronizeStores} from '@stores/SynchronizeStores'
import {computedFn, IDisposer} from 'mobx-utils'
// Don't move this import to global
import {CatalogInstanceChildStore} from './CatalogInstanceChildStore'

export class CatalogCardViewStore extends CatalogInstanceChildStore {
  _toDispose: Array<IDisposer> = []

  @action dispose = () => this._toDispose.forEach(d => d())

  constructor(public catalog: CatalogStore) {
    super(catalog)

    synchronizeStores(() => {
      this._toDispose.push(
        reaction(() => this.catalog.fieldsWithDefs, (attributes) => {
          if (!_.isEmpty(attributes)) {
            let cardViewObject = {} as ICardViewSettings
            _.forEach(attributes, (attr) => {
              cardViewObject[attr.name] = {show: false}
            })
            this.cardView = {...cardViewObject, ...this.cardView}
          }
        }, {name: 'Dashboard:  Add custom attributes to card view'}))
    })
  }

  get catalogConfig() { return this.catalog.catalogConfig}

  get workspaceConfig() { return this.catalog.workspaceConfig}

  get cardView() { return this.catalog.settings.cardView}

  set cardView(v) {
    this.catalog.settings.cardView = v
  }

  get sectionDynamicFields() { return this.catalog.sectionDynamicFields}

  get sectionValues() { return this.catalog.sectionValues}

  get sectionConfigFor() { return this.catalog.sectionConfigFor}

  get hasSection() { return this.catalog.hasSection}

  get sectionById() { return this.catalog.sectionById}

  @computed get cardView_isNothingSelected() {
    return this.cardView_noneSelected(null, this.cardView)
  }

  @computed get overriddenVisibleFields() {
    const {cardViewOrderOverrideLookup, originalVisibleFields} = this

    if (!_.isEmpty(cardViewOrderOverrideLookup)) {
      return _.orderBy(originalVisibleFields, f => cardViewOrderOverrideLookup[f.field] ?? originalVisibleFields.length)
    }
    return originalVisibleFields
  }

  @computed.struct get cardViewWithAttributes(): ICardViewSettings {
    const {catalog: {fieldsWithDefs}, cardView} = this
    return fieldsWithDefs ? {...fieldsWithDefs.filter(f => !f.section || f.section.split('.').length <= 2).reduce((r, ca) => _.assign(r, {[ca.name]: false}), {}), ...cardView} : cardView
  }

  get caDefByName() { return this.catalog.caDefByName}

  /**
   * Other fields we need to query for _source because the user has checked them in the card view
   */
  @computed get otherVisibleCardFields() {
    let {cardViewWithAttributes, _fieldByName, workspaceConfig, caDefByName} = this
    const checkedFields = Object.keys(cardViewWithAttributes || {}).filter(k => cardViewWithAttributes[k] && (k == 'all' || _fieldByName[k])) ?? []

    return _.uniq([
      ...checkedFields.filter(k => cardViewWithAttributes[k]?.show && !caDefByName[k] && k != 'all'),
      ..._.flatMap(checkedFields.map(f => _fieldByName[f]?.card?.titleFields)),
      ..._.flatMap(checkedFields.map(f => _fieldByName[f]?.card?.nestedTitleFields)),
      ..._.flatMap(checkedFields.map(f => _.values(_fieldByName[f]?.card?.nested?.cardDetails?.main?.image))),
      ..._.flatMap(checkedFields.map(f => _.values(_fieldByName[f]?.card?.nested?.cardDetails?.main?.dataMap))),
      ..._.flatMap(checkedFields.map(f => _.values(_fieldByName[f]?.card?.nested?.cardDetails?.secondary?.dataMap))),
      ..._.flatMap(checkedFields.map(f => _.values(_fieldByName[f]?.card?.nested?.cardDetails?.extra))),
      ..._.values(workspaceConfig?.dataMap ?? {}).filter(v => _.isString(v))
    ].filter(_.identity))
  }


  @computed get visibleAttributeDefinitionNames() {
    let {cardViewWithAttributes, catalog: {caDefByName}} = this
    // const tags = this.catalog && stores.catalog.tags
    // if (tags && tags.length > 0 && tags[0].displayName && cardViewWithAttributes[_.toLower(tags[0].displayName.plural)]) {
    //   customAttributeDefByName[_.toLower(tags[0].displayName.plural)] = tags[0]
    // }
    return Object.keys(cardViewWithAttributes || {}).filter(k => cardViewWithAttributes[k]?.show && caDefByName[k]).map(k => caDefByName[k].name)
  }

  @computed get visibleCardFields(): Array<ICatalogFieldConfig> {
    const {cardViewWithAttributes, catalog: {availableCardFields, labelForField}} = this
    return availableCardFields.filter(f => cardViewWithAttributes[f.field]?.show)
  }

  @computed get groupedVisibleCardFields(): any {
    const {visibleCardFields: fields, catalog: {_fieldGroups}} = this
    return _.groupBy(_.flatten(_fieldGroups.map(group => _.orderBy(fields, f => f.def?.displayName?.plural).map(f => {
      if (_.includes(group.fieldGroup, f.field)) {
        return {...f, groupLabel: group.label}
      }
    }).filter(_.identity))), 'groupLabel')
  }

  @computed get customCardViews() {
    const {catalogConfig: {cardView}, workspaceConfig: {customViews}} = this
    return [
      ...(cardView?.presets ?? []),
      ...(customViews ?? []).filter(cv => cv.catalog?.fields).map((cv, i) => ({label: cv.label, fields: cv.catalog.fields, id: cv._id ?? `cv_${i}`}))
    ] as Array<PredefinedCardViewConfig>
  }

  @action selectCustomCardView = (cv: PredefinedCardViewConfig, cardView?: ICardViewSettings) => {
    let toModify = cardView ?? this.cardView
    if (cv.section) {
      const {cardView} = this
      if (cv.dynamicValues) {
        const {section: {path, viewOptions: {field}}, dynamicValues} = cv,
          prefix = `${path}.${field}.`

        _.keys(cardView).filter(k => k.startsWith(prefix)).forEach(k => _.assign(cardView[k], {show: false}))
        dynamicValues.forEach(v => _.assign(cardView, {[`${prefix}${v}`]: {show: true}}))
      }
    } else {
      const {sectionDynamicFields} = this
      toModify = {
        ...Array.from(sectionDynamicFields.keys()).reduce((acc, f) => _.assign(acc, {[f]: this.cardView[f]}), {}),
        ...cv.fields.reduce((acc, f) => _.assign(acc, {[f]: true}), {})
      }

      if (!cardView) {
        this.cardView = toModify
      }
    }

    return toModify
  }

  @action cardView_clear = (section?: string, cardViewToModify?: ICardViewSettings) => {
    let cardView = _.cloneDeep(cardViewToModify ?? this.cardView)
    const {sectionDynamicFields, sectionConfigFor, sectionValues} = this,
      sectionConfig = sectionConfigFor(section)
    if (sectionConfig?.viewOptions?.field && sectionConfig?.viewOptions?.hideOnViewOptionsTab) {
      const results = sectionValues.get(section),
        {path, viewOptions: {field}} = sectionConfig

      results.forEach(({key}) => _.assign(cardView, {[`${path}.${field}.${key}`]: {show: false}}))
    } else if (section === 'all') {
      Object.keys(cardView).filter(k => cardView[k]).forEach(k => _.assign(cardView, {[k]: {show: false}}))
    } else {
      Object.keys(cardView)
        .filter(k => cardView[k] && ((!section && !sectionDynamicFields.has(k)) || this.hasSection({section, fieldName: k})))
        .forEach(k => _.assign(cardView, {[k]: {show: false}}))
    }
    // this.setCardView({...cardView})

    if (!cardViewToModify) {
      this.cardView = cardView
    }

    return cardView
  }

  @action cardView_selectAll = (section?: string, cardViewToModify?: ICardViewSettings) => {
    const sectionConfig = section ? this.sectionById[section] : null,
      {hasSection, sectionValues, stores: {presets}} = this

    // console.debug(`cardView_selectAll`, {section})
    let cardView = _.cloneDeep(cardViewToModify ?? this.cardView)
    if (sectionConfig?.viewOptions?.field && sectionConfig?.viewOptions?.hideOnViewOptionsTab) {
      const results = sectionValues.get(section),
        {path, viewOptions: {field}} = sectionConfig

      results.forEach(({key}) => _.assign(cardView, {[`${path}.${field}.${key}`]: {show: true}}))
    } else if (sectionConfig?.viewOptions?.field) {
      const results = sectionValues.get(section),
        {path, viewOptions: {field}} = sectionConfig
      results.forEach(({key}) => _.assign(cardView, {[`${path}.${field}.${key}`]: {show: true}}))
    } else {
      Object.keys(cardView).filter(k => hasSection({section, fieldName: k})).forEach(k => _.assign(cardView, {[k]: {show: true}}))
    }

    if (!cardViewToModify) {
      if (!_.isEqual(cardView, this.cardView)) {
        this.cardView = cardView
      }
      presets.loadPreset(null, null, false)
    }

    return cardView
  }

  get fieldByName() { return this.catalog.fieldByName}

  get _fieldByName() { return this.catalog._fieldByName}

  @action cardView_resetToDefault = (section?: string, cardViewToModify?: ICardViewSettings) => {
    // this.settings.imageSource.id = this.features.imageSources?.default?.id ?? null

    // console.debug(`cardView_resetToDefault`, {section})

    if (cardViewToModify) {
      _.values(cardViewToModify).forEach(v => v.show = false)
    }

    const {fieldByName, defaultCardView} = this,
      cardView = _.cloneDeep(cardViewToModify ?? this.cardView) || {}

    Object.keys(defaultCardView).filter(k => this.hasSection({section, fieldName: k})).forEach(k => {
      _.assign(cardView, {[k]: {show: fieldByName(k)?.card?.default === true}})
    })

    // From the view options dialog we don't directly allow mutating the global card view to avoid immediate triggering of queries/rerenders
    if (!cardViewToModify) {
      this.cardView = _.cloneDeep(cardView)
    }

    return cardView
  }

  @action setCardView(cardView?: ICardViewSettings) {
    this.cardView = _.cloneDeep(cardView)
  }

  @action toggleCardViewFieldVisible = (field: string, cardViewToModify?: ICardViewSettings) => {
    const cardView = _.cloneDeep(cardViewToModify ?? this.cardView)
    if (cardView[field]) {
      _.assign(cardView, {[field]: {show: cardView[field]?.show == false}})
    } else {
      cardView[field] = {show: true}
    }

    if (field === 'all') {  // If all is checked/unchecked then reset everyone else
      let setValue = cardView[field]?.show
      Object.keys(cardView).forEach(v => _.assign(cardView, {v: {show: setValue}}))
    } else {
      cardView.all = {show: true}
      if ((_.keys(cardView).length != _.keys(this.defaultCardView).length) || (!_.every(Object.values(cardView), Boolean))) {
        cardView.all = {show: false}
      }
    }

    if (!cardViewToModify) {
      this.stores.presets.loadPreset(null, null, false)
      this.setCardView(cardView)
    }

    return cardView
  }

  cardView_noneSelected = computedFn((section, cardView) => {
    return _.isEmpty(cardView) || Object.keys(cardView).filter(k => section === 'all' || !section || this.hasSection({section, fieldName: k})).every(k => !cardView[k.toString()])
  })

  get sectionPathFor() { return this.catalog.sectionPathFor}

  get availableCardFields() { return this.catalog.availableCardFields}

  //get visibleCardFields() { return this.catalog.visibleCardFields}

  get perspective() { return this.catalog.perspective}

  cardView_isCustomViewSelected = ({fields, dynamicValues, section}: PredefinedCardViewConfig, cardViewToVerify?: ICardViewSettings) => {
    const cardView = cardViewToVerify ?? this.cardView

    let r = true
    if (!_.isEmpty(dynamicValues)) {
      const path = `${this.sectionPathFor(section)}.`,
        dynamicFields = Object.keys(cardView).filter(k => k.startsWith(path)),
        fieldsSet = new Set(dynamicValues.map(v => `${path}${v}`))

      //console.log({dynamicValues, dynamicFields, fieldsSet})
      r = r && dynamicValues.every(k => cardView[`${path}${k}`]) && dynamicFields.every(k => cardView[k] ? fieldsSet.has(k) : !fieldsSet.has(k))
    }

    if (!_.isEmpty(fields)) {
      const lookup = new Set(fields),
        {sectionDynamicFields} = this
      r = r && Object.keys(cardView).every(k => cardView[k] ? lookup.has(k) || sectionDynamicFields.has(k) : !lookup.has(k))
    }

    return r
  }

  cardView_allSelected = computedFn((section, cardView) => {
    const {defaultCardView, sectionById, availableCardFields, sectionValues} = this,
      array = availableCardFields.filter(f => this.hasSection({section, fieldName: f.field})),
      r = !_.isEmpty(cardView) && !_.isEmpty(array) && array.every(f => cardView[f.field]?.show === true),
      sectionConfig = section ? sectionById[section] : null

    return r && (!sectionConfig?.viewOptions?.field || (sectionValues.has(section) && sectionValues.get(section).every(({key}) => cardView[`${this.sectionPathFor(sectionConfig)}.${key}`])))
  })

  @computed get nestedFields() {
    const {perspective, visibleCardFields} = this
    return visibleCardFields.filter(f => (f?.card?.nested || f?.esIndex) && f?.field != perspective?.field)
  }

  @computed get isNestedFieldAvailable() {
    const {
        catalog: {perspective, availableCardFields}
      } = this,
      allAvailableFields = availableCardFields
    return allAvailableFields.some(f => (f?.card?.nested || f?.esIndex) && f?.field != perspective?.field)
  }

  get settings() {
    return this.catalog.settings
  }

  get pathToField() { return this.catalog.pathToField}

  @computed get originalVisibleFields() {
    const {perspective, groupedVisibleCardFields, visibleCardFields, cardView, nestedFields, settings: {showNestedFieldsOnTopLevelCard}, catalog: {findFieldInTree}} = this,
      groupsVisibleFields = Object.keys(groupedVisibleCardFields)

    let fields = visibleCardFields,
      // If we have nested fields show them unless we are showing the nested cards - unless we're viewing that perspective

      r = fields.filter(f => {
        let fk = findFieldInTree(f.field, true)

        if (fk) {
          // verify there's not a higher FK
          let parentFK = fk
          do {
            parentFK = findFieldInTree(parentFK.field.substring(0, parentFK.field.lastIndexOf('.') - 1), true)
            if (parentFK) { fk = parentFK}
          } while (parentFK != null)
        }

        if (!fk || fk === f || showNestedFieldsOnTopLevelCard || !cardView[fk.field]?.show) return true
        if (fk && !showNestedFieldsOnTopLevelCard) return false

        if (groupsVisibleFields.length > 0) return !groupsVisibleFields.some(v => _.some(groupedVisibleCardFields[v], g => g.field === f.field))

        return true
      })


    if (perspective?.field) {
      let nestedPrefix = perspective?.field ? `${perspective.field}.` : ''
      r = _.orderBy(r, f => !f.field.startsWith(nestedPrefix))
    }

    return r
  }

  @computed get cardViewOrderOverrideLookup(): { [field: string]: number } {
    const {catalog: {settings: {cardViewOrderOverride}, perspectiveId}} = this
    if (!cardViewOrderOverride || !perspectiveId || _.isEmpty(cardViewOrderOverride[perspectiveId])) return {}

    return (cardViewOrderOverride[perspectiveId] ?? []).reduce((acc, v, i) => _.assign(acc, {[v]: i}), {})
  }

  @computed get cardViewSelectedKeys() {
    const {cardView} = this
    return _.keys(cardView).filter(k => cardView[k].show)
  }

  @computed get defaultCardView(): ICardViewSettings {
    const {catalog: {availableCardFields, pdfCardFields}} = this,
      r: ICardViewSettings = [...availableCardFields, ...pdfCardFields]
        .filter(v => v?.card?.default).reduce((acc, v) => _.merge(acc, {[v.field]: {show:true}} as ICardViewFieldSettings), {all: {show: false}})

    return r
  }

  @action resetCardViewToDefault = () => {
    this.cardView = _.cloneDeep(this.defaultCardView) // Make sure to copy!
  }

  @action collapseAllVisibleCardFields = () => {
    const {visibleCardFields, settings: {cardView}} = this
    visibleCardFields.forEach(({field}) => {
      cardView[field] = {...(cardView[field] ?? {}), collapsed: true}
    })
  }

  @action expandAllVisibleCardFields = () => {
    const {visibleCardFields, settings: {cardView}} = this
    visibleCardFields.forEach(({field}) => {
      cardView[field] = {...(cardView[field] ?? {}), collapsed: false}
    })
  }

  cardView_toggleOff = (field: string) => {
    const {cardView, visibleCardFields} = this
    _.assign(cardView, {[field]: {show: false}})
    visibleCardFields.forEach(f => {
      if (f.field.startsWith((`${field}.`))) {
        _.assign(cardView, {[f.field]: {show: false}})
      }
    })
  }

}


// WEBPACK FOOTER //
// ./src/stores/catalog/CatalogCardViewStore.ts